/* ==================================================
   ROSIE & B — FRAGRANCE FINDER SENTENCE BANK

   STRUCTURAL RULES

   1. No pool may contain fewer templates than the
      maximum number of cards that may require it.

      Current limits:
      - Normal contexts: max 4 cards
      - Broad contexts: max 6 cards

      Therefore:
      - Any bullet pool must contain >= 6 templates
        if it can be used in broad contexts.
      - Otherwise >= 4 templates minimum.

   2. Templates must not depend on tokens that the
      engine cannot guarantee to provide.
      (See sentenceIsEligible rules.)

   3. Templates must not duplicate tone or structure
      so closely that reuse across sessions feels
      repetitive.

   4. Expression templates:

      - "one" templates may only reference {char1}.
      - "two" templates may reference {char1} and/or {char2}.
      - "three" templates may reference {char1}, {char2}, and/or {char3}.

      Templates must never reference a {charX} that would not
      exist for that pool size.

      Fewer characteristics may be referenced for stylistic
      variety (e.g. "Distinctively {char1}." in the "two" template).

   5. familyFamilies flag:
      Templates using |familyFamilies must include
      the word "family" (singular) so the grammar
      engine can pluralise when needed.

   6. Descriptor pools must assume conflict filtering
      via WORD_CONFLICTS — avoid near-synonym stacking
      in the same template.

   7. Do not reduce pool sizes without checking:
      - Max cards
      - Broad contexts
      - Enumeration mode behaviour

   ================================================== */

window.rbSentenceBank = {

  /* ---------------------------------------------
     A. STEP PANELS
  --------------------------------------------- */

  step_title: {

    product: {
      primary: "Which type of fragrance product are you interested in?",
      secondary: "Would you like to focus on a specific product range?"
    },

    preferences: {
      primary: "What do you usually gravitate towards in a fragrance?",
      secondary: "Are there any scent qualities you are drawn to?"
    },

    space: {
      primary: "Which room or space would you like to fragrance?",
      secondary: "Do you have a space in mind?"
    },

    results: {
      primary: "Recommended For You"
    },

    saved_results: {
      singular: "Your Saved Recommendation",
      plural: "Your Saved Recommendations"
    }
  },

  step_intro: {

    product: {
      primary: "Choose from Reed Diffuser, Wax Melts, or Hand & Body.",
      secondary: "This step is optional."
    },

    preferences: {
      primary: "Select up to 4 options. Choose the 'families' and 'characteristics' that feel right together.",
      secondary: "Select up to 4 options that feel right together. Or skip this step if you're unsure."
    },

    space: {
      primary: "Select 1 option.",
      secondary: "This is optional - you can continue without making a selection."
    },

    saved_results: {
      singular: {
        first_para: "Here is the recommendation you saved {savedWhen}.",
        second_para: "Please note that next time you save, this recommendation will be overwritten."
      },
      plural: {
        first_para: "Here are the recommendations you saved {savedWhen}.",
        second_para: "Please note that next time you save, these recommendations will be overwritten."
      }
    }
  },

  /* ---------------------------------------------
     B. GUIDANCE
  --------------------------------------------- */

  guidance: {

    preferences: {
      selection_count: [
        "Two or three options is often a good balance."
      ],
      toggleable: [
        "The above options can be selected and also deselected."
      ],
      combination: [
        "You can select 'family' and 'characteristic' options in any combination."
      ]
    },

    preferences_filtered_by_product: {
      hand_body: [
        "Some families and characteristics are not available here because they aren't expressed within our Hand & Body range."
      ]
    },

    navigation: {
      step_indicators: [
        "You can use the circular indicators above to move backwards and adjust your selection(s)."
      ]
    },

    space: {
      filtered_by_product: {
        wax_melts: [
          "The Cloakroom option isn't available here because this type of space isn't suited to wax melt products."
        ],
        hand_body: [
          "Some options aren't available here because the spaces aren't perfectly suited to hand & body products."
        ]
      }
    },

    filtered_by_room_product: {

      wax_melts: {
        cloakroom: [
          "The Wax Melts option isn’t available here because this type of product is not intended for use in cloakrooms."
        ],
        bathroom: [
          "The Wax Melts option isn’t available here because this type of product is not intended for use in bathrooms."
        ]
      },

      hand_body: {
        hallway: [
          "The Hand & Body option isn’t available here because these types of product are not intended for use in hallways."
        ],
        living_room: [
          "The Hand & Body option isn’t available here because these types of product are not intended for use in living rooms."
        ],
        home_office: [
          "The Hand & Body option isn’t available here because these types of product are not intended for use in home offices."
        ],
        snug_family_room: [
          "The Hand & Body option isn’t available here because these types of product are not intended for use in snugs or family rooms."
        ],
        open_plan: [
          "The Hand & Body option isn’t available here because these types of product are not intended for use in open plan spaces."
        ],
        dining_room: [
          "The Hand & Body option isn’t available here because these types of product are not intended for use in dining rooms."
        ]
      }

    }
  },

  /* ---------------------------------------------
     C. RECOMMENDATIONS - TOP PANEL
  --------------------------------------------- */

  recommendations: {

    /* -------- PRIMARY STATES (count-aware) -------- */

    top_panel: {

      empty_result: [
        "We don’t currently offer a Rosie & B fragrance that matches this exact combination. You may like to adjust your selections or start again.",
        "The selections you made do not currently align with a Rosie & B fragrance. You may like to adjust your selections or start again."
      ],
      
      intent_alignment_single_preference_one_card: [
        "You selected {preference|cap|em}, which doesn’t feature in the fragrance shown below — instead, this recommendation reflects other selections you made.",
        "Although {preference|cap|em} was part of your selection, the fragrance shown below leans towards other options you chose.",
        "While {preference|cap|em} hasn't shaped our recommendation, the fragrance shown below reflects the rest of the options you selected.",
        "The fragrance below reflects your preferences, although {preference|cap|em} isn’t a defining quality in this result.",
        "Although you included {preference|cap|em} in your selection, the fragrance shown here focuses more on other selections you made."
      ],

      intent_alignment_single_preference_multiple_cards: [
        "You selected {preference|cap|em}, which doesn’t feature in any of the fragrances shown below — instead, these recommendations reflect the other selections you made.",
        "Although {preference|cap|em} was part of your selection, the fragrances shown below lean towards the other options you chose.",
        "While {preference|cap|em} hasn't shaped our recommendations, the fragrances shown below reflect the rest of the options you selected.",
        "The fragrances below reflect your preferences, although {preference|cap|em} isn’t a defining quality in these results.",
        "Although you included {preference|cap|em} in your selection, the fragrances shown here focus more on other selections you made."
      ],

      intent_alignment_multiple_preferences_one_card: [
        "You selected {preferences|cap|em}. These don’t feature in the fragrance shown below — instead, this recommendation reflects other selections you made.",
        "Although {preferences|cap|em} were part of your selection, the fragrance shown below leans more towards other options you chose.",
        "While {preferences|cap|em} haven't shaped our recommendations, the fragrance shown below reflects the rest of the options you selected.",
        "The fragrance below reflects your preferences, although {preferences|cap|em} aren’t defining qualities in this result.",
        "Although you included {preferences|cap|em} in your selection, the fragrance shown here focuses more on other selections you made."
      ],

      intent_alignment_multiple_preferences_multiple_cards: [
        "You selected {preferences|cap|em}. These don’t feature in any of the fragrances shown below — instead, these recommendations reflect other selections you made.",
        "Although {preferences|cap|em} were part of your selection, the fragrances shown below lean more towards other options you chose.",
        "While {preferences|cap|em} haven't shaped our recommendations, the fragrances shown below reflect the rest of the options you selected.",
        "The fragrances below reflect your preferences, although {preferences|cap|em} aren’t defining qualities in these results.",
        "Although you included {preferences|cap|em} in your selection, the fragrances shown here focus more on other selections you made."
      ],

      exact_match_one_card: [
        "One Rosie & B fragrance aligns fully with every selection you made.",
        "Based on what you selected, one fragrance matches your preferences in full.",
        "We’d like to recommend one Rosie & B fragrance that fully reflects all the selections you made.",
        "There is one of our fragrances that reflects every aspect of what you chose.",
        "We're delighted to recommend a Rosie & B fragrance that corresponds exactly with your selections.",
        "Your selections point to a fragrance that aligns beautifully with what you're looking for."
      ],

      exact_match_two_cards: [
        "Two Rosie & B fragrances align nicely with every selection you made.",
        "Based on what you selected, two fragrances match your preferences perfectly.",
        "We’d like to recommend two Rosie & B fragrances that fully reflect the selections you made.",
        "Here are two Rosie & B fragrances — both aligned with everything you selected.",
        "The two fragrances shown below correspond exactly with the selections you made.",
        "Both fragrances shown below mirror your selections exactly.",
        "Two Rosie & B fragrances reflect all aspects of what you selected."
      ],

      exact_match_three_cards: [
        "Three Rosie & B fragrances align nicely with every selection you made.",
        "Based on what you selected, three fragrances match your preferences in full.",
        "We’re delighted to recommend three of our fragrances that fully reflect the selections you made.",
        "We'd like to recommend three Rosie & B fragrances — all three reflect your selections in full",
        "The three fragrances shown below exactly mirror the selections you made.",
        "All three fragrances shown below correspond exactly with your selections.",
        "Three Rosie & B fragrances reflect all aspects of what you selected."
      ],

      exact_match_four_cards: [
        "Four Rosie & B fragrances align nicely with every selection you made.",
        "Based on what you selected, four fragrances match your preferences in full.",
        "Your selections point precisely to four fragrances that reflect them entirely.",
        "We’re delighted to recommend four Rosie & B fragrances that fully reflect the selections you made.",
        "All four fragrances shown below correspond exactly with your selections.",
        "Four Rosie & B fragrances reflect all aspects of what you selected."
      ],

      single_result: [
        "One Rosie & B fragrance in particular aligns closely with what you selected.",
        "Based on what you selected, there is one fragrance that feels like a perfect fit.",
        "Based on your choices, there is one fragrance that feels like an excellent fit.",
        "We'd like to recommend one particular Rosie & B fragrance that aligns nicely with your preferences.",
        "What you selected points very clearly to a single Rosie & B fragrance.",
        "There is one Rosie & B fragrance that is a particularly strong match for your selections.",
        "There is one fragrance in particular that is aligned with the qualities you are drawn to.",
        "Based on what you selected, there is one fragrance that is the most compelling overall.",
        "There is one fragrance in particular that we think you will love.",
        "One Rosie & B fragrance aligns especially well with what you selected.",
        "From what you selected, we'd like to recommend one Rosie & B fragrance."
      ],

      single_result_single_selection: [
        "There is one Rosie & B fragrance in particular that we think you will love.",
        "Based on the option you selected, there is one fragrance that stands out.",
        "One Rosie & B fragrance aligns with what you selected.",
        "Your selection points to one Rosie & B fragrance.",
        "There is one Rosie & B fragrance that matches what you chose.",
        "One Rosie & B fragrance corresponds with your selection."
      ],

      two_results: [
        "There are two Rosie & B fragrances that align closely with what you selected.",
        "Based on what you selected, there are two fragrances we'd like to recommend.",
        "Based on your preferences, there are two Rosie & B fragrances we'd like to recommend.",
        "There are two Rosie & B fragrances that we'd like to recommend.",
        "There are two fragrances that are aligned with the qualities you are drawn to.",
        "We'd like to recommend two fragrances that align nicely with your preferences.",
        "There are two fragrances in particular that we think you will love.",
        "From what you selected, we'd like to recommend two Rosie & B fragrances.",
        "Here are two fragrances that reflect your preferences, and we think you will love.",
        "Based on your preferences, there are two fragrances that we think you'll love.",
        "Based on what you selected, there are two Rosie & B fragrances that are the most compelling overall."
      ],

      two_results_single_selection: [
        "There are two Rosie & B fragrances in particular that we think you will love.",
        "Based on the option you selected, there are two fragrances that stand out.",
        "Based on what you selected, there are two fragrances that are especially suitable.",
        "Your selection points to two Rosie & B fragrances.",
        "There are two fragrances that correspond well with your selection.",
        "There are two Rosie & B fragrances that match what you chose.",
        "Two Rosie & B fragrances are well suited to your selection."
      ],

      three_results: [
        "Three Rosie & B fragrances align closely with what you selected.",
        "Based on what you selected, we'd like to recommend three fragrances.",
        "Based on what you selected, there are three fragrances we'd like to recommend.",
        "Based on your preferences, here are three fragrances that we think you'll love.",
        "There are three fragrances that are aligned with the qualities you are drawn to.",
        "We'd like to recommend a few fragrances that align nicely with your preferences.",
        "From what you selected, we'd like to recommend three Rosie & B fragrances.",
        "Here are three Rosie & B fragrances for you to explore.",
        "There are three Rosie & B fragrances that you may like to explore.",
        "Here are three fragrances that we think you will love."
      ],

      three_results_single_selection: [
        "There are three Rosie & B fragrances in particular that we think you will love.",
        "Based on the option you selected, there are three fragrances that stand out.",
        "Based on what you selected, there are three fragrances that are especially suitable.",
        "Your selection points to three Rosie & B fragrances.",
        "There are three fragrances that correspond well with your selection.",
        "There are three Rosie & B fragrances that match what you chose.",
        "Three Rosie & B fragrances are well suited to your selection."
      ],

      four_results: [
        "Four Rosie & B fragrances align closely with what you selected.",
        "Based on what you selected, we'd like to recommend four fragrances.",
        "Based on what you selected, there are four fragrances we'd like to recommend.",
        "There are four fragrances that are aligned with the qualities you are drawn to.",
        "We'd like to recommend a few fragrances that align nicely with your preferences.",
        "Based on your preferences, here are four fragrances for you to consider.",
        "From what you selected, a small number of fragrances align nicely.",
        "From what you selected, we'd like to recommend four Rosie & B fragrances.",
        "Here are four Rosie & B fragrances for you to explore.",
        "Here are four fragrances that reflect your preferences, and we think you will love."
      ],

      four_results_single_selection: [
        "There are four Rosie & B fragrances in particular that we think you will love.",
        "Based on the option you selected, there are four fragrances that stand out.",
        "Based on what you selected, there are four fragrances that are especially suitable.",
        "Here are four of our fragrances for you to explore, each aligning with what you selected.",
        "Your selection points to four Rosie & B fragrances.",
        "There are four fragrances that correspond well with your selection.",
        "There are four Rosie & B fragrances that match what you chose.",
        "Four Rosie & B fragrances are well suited to your selection."
      ],

      five_results: [
        "Based on what you selected, these five fragrances are strong matches.",
        "Several Rosie & B fragrances align well with what you selected, and are shown below.",
        "From what you selected, five fragrances are well worth considering.",
        "Here are five of our fragrances for you to explore — they align with what you selected.",
        "Several of our fragrances align closely with what you selected."
      ],

      six_results: [
        "Based on what you selected, six fragrances are strong matches.",
        "Several Rosie & B fragrances align well with what you selected, and are shown below.",
        "From what you selected, six fragrances are well worth considering.",
        "Here are six of our fragrances for you to explore — they align with what you selected.",
        "Based on what you selected, the six fragrances below sit very closely together.",
        "Several of our fragrances align closely with what you selected."
      ],

      /* -------- STANDOUT VARIANTS -------- */

      standout_with_one_supporting: [
        "There is one Rosie & B fragrance that stands out, with another that may also appeal.",
        "One of our fragrances emerges as a strong overall match, and there is one other that you might like to consider.",
        "We'd like to recommend one fragrance in particular, alongside another that you might like to consider.",
        "We'd like to recommend two fragrances, with one standing out in particular.",
        "There are two fragrances that align with your preferences, with one standing out in particular."
      ],

      standout_with_two_supporting: [
        "One Rosie & B fragrance stands out, with two other options that may also appeal.",
        "One of our fragrances emerges as the strongest overall match, and there are two others that you might like to consider.",
        "We'd like to recommend one fragrance in particular, alongside two others that you might like to consider.",
        "Here are three fragrances that align with your preferences, with one in particular that we'd like to recommend.",
        "Here are three fragrances that align with your preferences, with one standing out in particular."
      ],

      standout_with_three_supporting: [
        "One Rosie & B fragrance in particular stands out, supported by three other options worth exploring.",
        "One of our fragrances emerges as a strong overall match, and there are three others that you might like to consider.",
        "We'd like to recommend one fragrance in particular, alongside three others that you might like to explore.",
        "Here are four fragrances that align with your preferences, with one in particular that we are delighted to recommend.",
        "Here are a few fragrances that align with your preferences, with one standing out in particular."
      ],

      /* -------- CLOSEST MATCH -------- */

      closest_single: [
        "While no Rosie & B fragrance aligns perfectly, the option below reflects your preferences closely.",
        "While not a perfect fit, the fragrance shown below comes closest to the qualities you are drawn to.",
        "Based on your preferences, the fragrance shown below is the closest overall fit."
      ],

      closest_multiple: [
        "While no Rosie & B fragrance aligns perfectly, the options below reflect your preferences closely.",
        "While not perfect fits, the fragrances shown below come closest to the qualities you are drawn to.",
        "Based on your preferences, the fragrances shown below are the closest overall fit."
      ]
    },

    /* -------- SUPPLEMENTARY EXPLANATIONS -------- */

    supplementary: {

      product_only: {
        singular: [
          "This is a suggestion within your chosen product range — you can refine further by adding fragrance preferences or a space:"
        ],
        plural: [
          "These are suggestions within your chosen product range — you can refine further by adding fragrance preferences or a space:"
        ]
      },

      one_preference_only: {
        singular: [
          "This recommendation reflects the single fragrance preference you chose — you can refine further by adding more detail."
        ],
        plural: [
          "These recommendations reflect the single fragrance preference you chose — you can refine further by adding more detail."
        ]
      },

      space_only: {
        singular: [
          "This recommendation is suited to your chosen space — you can refine further by adding fragrance preferences or a product type."
        ],
        plural: [
          "These recommendations are suited to your chosen space — you can refine further by adding fragrance preferences or a product type."
        ]
      }
    },

    refinement_prompt: {

      intent_alignment: [
        "You may enjoy running the Fragrance Finder again with a slightly more focused selection."
      ],

      closest_match: [
        "You may enjoy running the Fragrance Finder again with a more focused selection to explore combinations that align more closely."
      ]
    },

    save_prompt: {
      singular: [
        "<p>Saving this recommendation ensures you can easily come back to it if you move away from the Fragrance Finder.</p><p>Use the [bookmark-icon] icon to view your saved recommendations at any time. Each new save replaces the previous one.</p>"
      ],
      plural: [
        "<p>Saving these recommendations ensures you can easily come back to them if you move away from the Fragrance Finder.</p><p>Use the [bookmark-icon] icon to view your saved recommendations at any time. Each new save replaces the previous one.</p>"
      ]
    }
  },

  /* ---------------------------------------------
     D. RECOMMENDATION CARDS - BULLETS
  --------------------------------------------- */

  card: {

    /* -------- FAMILIES LOGIC -------- */

    match: {

      family: [
        "Matches your preference for {families|cap|em} fragrances.",
        "Reflects your enjoyment of {families|cap|em} fragrances.",
        "A lovely choice for those who enjoy fragrances in the {families|cap|em|familyFamilies} family.",
        "{descriptor|article} scent that is ideal if you enjoy {families|cap|em} fragrances.",
        "{families|cap|em|article} fragrance, shaped by its {descriptor} character.",
        "Ideal if you are drawn to {families|cap|em} scent profiles.",
        "For those who are drawn to {families|cap|em} notes.",
        "{descriptor|article} fragrance in the {families|cap|em|familyFamilies} family.",
        "A super choice for lovers of {families|cap|em} family scents."
      ]
    },

    /* -------- SPACE LOGIC -------- */

    space: {

      /* Direct match: user selected this space AND fragrance supports it */
      direct: [
        "Well suited to {space|plural}.",
        "An excellent choice for {space|plural}.",
        "A lovely fragrance for {space|article}.",
        "You selected {space|label|em} and this fragrance is a lovely fit.",
        "You selected {space|label|em} and this scent is a super choice.",
        "A lovely choice for {space|plural}.",
        "With your selection of {space|label|em} in mind, this fragrance is a lovely choice.",
        "Recommended with your {space|label|em} selection in mind.",
        "A fitting choice for {space|article} setting.",
        "A natural fit for {space|plural}."
      ],

      /* Only used when fragrance supports many spaces (n or more) */
      versatile: [
        "A versatile fragrance that works beautifully in a range of spaces, including {space|plural}.",
        "Well suited to a variety of spaces, with your choice of {space|label|em} being a good fit.",
        "Designed to work beautifully across multiple spaces, including {space|plural}.",
        "A natural fit for a range of spaces, including {space|plural}.",
        "Comfortably at home in different spaces, including {related_spaces|plural}.",
        "A versatile scent that is lovely in different spaces, including {space|plural}.",
        "A scent that adapts beautifully to different spaces, including {space|plural}.",
        "Suitable for a variety of spaces, including {related_spaces|plural}."
      ],

      /* No direct match, but fragrance still appears in results */
      contextual: [
        "Well suited to spaces such as {related_spaces|plural}.",
        "Often chosen for spaces such as {related_spaces|plural}.",
        "A popular choice for spaces such as {related_spaces|plural}.",
        "Suitable for spaces such as {related_spaces|plural}.",
        "A fragrance that works beautifully in spaces such as {related_spaces|plural}.",
        "Works beautifully in {related_spaces|plural}."
      ],

      hand_body: {
        general: [
          "A lovely choice for {space|article}.",
          "A welcome presence in {space|article}.",
          "A natural fit for {space|plural}.",
          "You selected {space|label|em} and this is a lovely fit.",
          "A lovely hand & body scent for {space|plural}.",
          "A gorgeous addition to {space|article}."
        ],

        bedroom: [
          "A lovely fragrance to keep in {space|article} in lotion form.",
          "A beautiful scent to enjoy in lotion form and keep in your {space}.",
          "You selected {space|label|em} and this lotion scent is a perfect choice.",
          "A fragrance to enjoy as hand & body lotion in {space|article}.",
          "A lovely lotion fragrance for {space|article}."
        ]
      }
    },

    /* -------- EXPRESSION LOGIC -------- */

    expression: {

      templates: {

        one: [
          "{descriptor|article}, {char1} fragrance.",
          "{descriptor|article} and {char1} blend.",
          "{char1|article} and {descriptor} fragrance.",
          "A scent that is {descriptor} and {char1}.",
          "{descriptor|article} scent.",
          "{char1|article} fragrance.",
          "{char1|cap} and {descriptor}.",
          "Defined by its {char1} character.",
          "{descriptor|article} blend.",
          "{descriptor|article} fragrance with {descriptor|article} character.",
          "{char1|article} and {descriptor} scent.",
          "{descriptor|article} fragrance that is defined by its {char1} character."
        ],

        two: [
          "{descriptor|article} fragrance — {char1} and {char2}.",
          "{descriptor|article} blend that is {char1} and {char2}.",
          "{descriptor|article} scent that is {char1} and {char2}.",
          "Both {char1} and {char2}, this is {descriptor|article} fragrance.",
          "This fragrance is both {char1} and {char2}.",
          "Distinctively {char1}.",
          "A beautifully balanced blend, with {char1} and {char2} characteristics.",
          "A fragrance that balances its {char1} and {char2} characteristics.",
          "{char1|cap}, {char2} and {descriptor}.",
          "{char1|cap}. {char2|cap}.",
          "A beautifully balanced scent, that brings together {char1} and {char2} qualities.",
          "Defined by both {char1} and {char2} characteristics, this is {descriptor|article} fragrance.",
          "A lovely choice when you want something that is {char1} and {char2}.",
          "A scent defined by its {char1} and {char2} characteristics.",
          "A fragrance that is {char1} and {char2}.",
          "A scent that is both {char1} and {char2}.",
          "{descriptor|article} fragrance that is defined by its {char1} and {char2} character."
        ],

        three: [
          "{descriptor|article} fragrance that is {char1}, {char2} and {char3}.",
          "{descriptor|article} blend that is {char1}, {char2} and {char3}.",
          "{descriptor|article} scent that is {char1}, {char2} and {char3}.",
          "A fragrance that balances its {char1}, {char2} and {char3} characteristics.",
          "{descriptor|article} fragrance that is defined by its {char1}, {char2} and {char3} characteristics.",
          "{char1|cap}. {char2|cap}. {char3|cap}.",
          "A wonderful combination of {char1}, {char2} and {char3} qualities.",
          "A beautifully balanced scent, that brings together {char1}, {char2} and {char3} qualities.",
          "Unapologetically {char1}, {char2} and {char3}.",
          "A fragrance defined by its {char1}, {char2} and {char3} character.",
          "A lovely choice when you want something {char1}, {char2} and {char3}.",
          "A scent defined by its {char1}, {char2} and {char3} character."
        ]
      }

    }
  },

  /* ---------------------------------------------
     E. PRODUCT INFORMATION
  --------------------------------------------- */

  product_info: {

    suitability: {

      reed_diffuser: [
        "Ideal for {related_spaces|plural} where you want a long-lasting presence.",
        "Offers steady and continuous fragrance and is well suited to {related_spaces|plural}.",
        "A long-lasting, {descriptor} reed diffuser scent with {families} notes.",
        "A long-lasting reed diffuser fragrance with {families} notes.",
        "A low-maintenance way to enjoy {families} fragrance in your home.",
        "{descriptor|article} scent for {related_spaces|plural}, and a low-maintenance way to enjoy gorgeous scent.",
        "Enjoy consistent and long-lasting fragrance - a lovely choice for {related_spaces|plural}."
      ],

      wax_melts: [
        "{descriptor|article} scent that's a great choice for {related_spaces|plural}.",
        "Space-filling and perfect for {related_spaces|plural}.",
        "Fill your {space} with this stunning wax melt fragrance.",
        "A wonderful wax melt fragrance with {families} notes.",
        "A distinctive wax melt scent for {related_spaces|plural}.",
        "A gorgeous {families} fragrance that's perfect for {related_spaces|plural}.",
        "{descriptor|article} scent that's perfect for {related_spaces|plural}."
      ],

      hand_body_set: [
        "Beautifully scented and skin-loving — a luxurious hand & body set for your daily routine.",
        "An exquisite {families} hand & body set for your {space}.",
        "{descriptor|article} hand & body set crafted to leave skin soft, smooth, and delicately fragranced.",
        "A two-piece set of beautifully fragranced hand wash and hand & body lotion.",
        "A hand wash and lotion pairing that will bring beautiful fragrance to your {space}.",
        "Perfect for your {space} and crafted to leave your skin feeling soft and smooth.",
        "Refreshing hand wash and nourishing lotion, bringing together gentle care and elegant fragrance.",
        "{families|article} hand wash and lotion that leave your skin refreshed and nourished."
      ],

      hand_wash: [
        "A beautifully scented hand wash for your {space}.",
        "A delightfully fragranced and refreshing hand wash.",
        "{families|article} hand wash, crafted to bring moments of calm to your day.",
        "{families|article} hand wash that leaves your skin feeling cleansed and refreshed.",
        "A refreshing hand wash that gently cleanses while leaving a soft, lingering fragrance.",
        "Indulge in the gorgeous fragrance of this {descriptor} hand wash.",
        "{descriptor|article} hand wash with a refined and elegant fragrance.",
        "A sublime hand wash with {descriptor|article} scent."
      ],

      hand_body_lotion: [
        "A nourishing hand & body lotion with {descriptor|article} scent.",
        "Beautifully scented lotion designed to hydrate and delicately fragrance your skin.",
        "{descriptor|article} hand & body lotion crafted to leave your skin feeling soft and smooth.",
        "A smooth and nourishing lotion with a beautifully balanced {descriptor} fragrance.",
        "A beautifully balanced lotion that leaves skin soft, smooth, and delicately fragranced.",
        "Skin-loving and elegantly scented, with a beautifully refined fragrance.",
        "A moisturising hand & body lotion and a perfect addition to your {space}."
      ]
    },

    special_highlight: {
      "coconut-oakmoss-tonka": {
        reed_diffuser: [
          "This fragrance comes into its own - and is exquisite - in smaller spaces such as cloakrooms and bathrooms."
        ]
      },
      "fig-tuberose-oakmoss": {
        reed_diffuser: [
          "A Rosie & B best seller."
        ]
      },
      "honey-sandalwood-patchouli": {
        reed_diffuser: [
          "A beautiful fragrance with warmth and depth, that's perfect for bringing a sense of calm to small and medium-sized rooms."
        ]
      },
      "pear-jasmine-patchouli": {
        reed_diffuser: [
          "A Rosie & B best seller."
        ]
      },
      "orange-nutmeg-clove": {
        wax_melts: [
          "A Rosie & B best seller at this time of year."
        ]
      },
      "peony-clove-oud": {
        hand_body_set: [
          "A lighter hand & body fragrance for lovers of floral scents."
        ]
      },
      "rhubarb-plum": {
        reed_diffuser: [
          "A stunning combination of the sweetness of ripe plum and the tartness of rhubarb."
        ]
      }
    }
  }
};